import React from 'react'
import PropTypes from 'prop-types'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined'
import PriorityHighIcon from '@mui/icons-material/PriorityHigh'
import SosOutlinedIcon from '@mui/icons-material/SosOutlined'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
    LIFECYCLE,
    LIFECYCLE_LABEL,
    TAGS,
    tagLabel,
} from '../../helpers/trainerNotifications'
import {
    GRADIENT,
    ICON,
    ON_ORANGE,
    RADIUS,
    TAG,
    TEXT,
} from '../../theme/tokens'

/** The icon each tag carries, on its pill and in the strip preview. */
export const TAG_ICON = {
    URGENT: WarningAmberIcon,
    UPDATE: NotificationsNoneOutlinedIcon,
    IMPORTANT: PriorityHighIcon,
    MANDATORY: AssignmentTurnedInOutlinedIcon,
    SOS: SosOutlinedIcon,
}

const LIFECYCLE_TONE = {
    [LIFECYCLE.DRAFT]: TAG.neutral,
    [LIFECYCLE.SCHEDULED]: TAG.pinpoint,
    [LIFECYCLE.ACTIVE]: TAG.success,
    [LIFECYCLE.EXPIRED]: TAG.warning,
}

const pillSx = (tone) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.5,
    px: 1,
    py: 0.25,
    borderRadius: `${RADIUS.sm}px`,
    typography: 'caption',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    color: tone.fg,
    background: tone.bg,
})

/** Draft, Scheduled, Active or Expired, coloured by state. */
export const LifecycleChip = ({ lifecycle }) => (
    <Box component="span" sx={pillSx(LIFECYCLE_TONE[lifecycle] ?? TAG.neutral)}>
        {LIFECYCLE_LABEL[lifecycle] ?? lifecycle ?? '-'}
    </Box>
)
LifecycleChip.propTypes = { lifecycle: PropTypes.string }

/** A tag with its icon, in the tag's colour. */
export const TagPill = ({ tag }) => {
    if (!tag) return <Typography sx={{ typography: 'caption' }}>-</Typography>
    const Icon = TAG_ICON[tag]
    const tone = TAG[TAGS.find((t) => t.value === tag)?.tone] ?? TAG.neutral
    return (
        <Box component="span" sx={pillSx(tone)}>
            {Icon && <Icon sx={{ fontSize: ICON.sm }} />}
            {tagLabel(tag)}
        </Box>
    )
}
TagPill.propTypes = { tag: PropTypes.string }

/** Icon, title and subtitle at the top of each trainer notification page, with optional actions. */
export const NotificationHeader = ({ title, subtitle, actions = null }) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 3,
            flexWrap: 'wrap',
        }}
    >
        <Box
            sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}
        >
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    borderRadius: `${RADIUS.md}px`,
                    background: GRADIENT.brand,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}
            >
                <NotificationsActiveIcon
                    sx={{ color: ON_ORANGE, fontSize: ICON.xl }}
                />
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    component="h1"
                    sx={{
                        typography: 'h2',
                        fontWeight: 700,
                        color: TEXT.primary,
                        lineHeight: 1.2,
                    }}
                >
                    {title}
                </Typography>
                {subtitle && (
                    <Typography
                        sx={{
                            typography: 'caption',
                            color: TEXT.muted,
                            mt: 0.25,
                        }}
                    >
                        {subtitle}
                    </Typography>
                )}
            </Box>
        </Box>
        {actions && (
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                {actions}
            </Box>
        )}
    </Box>
)
NotificationHeader.propTypes = {
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    actions: PropTypes.node,
}
